import {
  timer as observableTimer,
  interval as observableInterval,
  of as observableOf,
  Observable,
  BehaviorSubject,
  ReplaySubject,
  Subject,
} from 'rxjs';

import { map, mergeMap, tap, takeUntil, catchError, retryWhen, delay, take } from 'rxjs/operators';
import { Injectable, inject, OnDestroy, signal, computed } from '@angular/core';
import { HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';

import { ApiService } from './api.service';
import { StorageService } from './storage.service';
import { User } from '../models';
import { ToasterService } from './toaster.service';


// Token refresh constants
const TOKEN_REFRESH = {
  THRESHOLD_SECONDS: 10,
  RETRY_ATTEMPTS: 2,
  RETRY_DELAY: 2000
};

// Interface definitions for better type safety
interface RefreshQueueItem {
  resolve: (data: AuthToken) => void;
  reject: (error: Error) => void;
}

interface AuthToken {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
}

interface PerformanceMetrics {
  cacheStatus: {
    countriesCached: boolean;
    countriesCacheAge: number | null;
    orderOptionsCached: boolean;
    orderOptionsCacheAge: number | null;
    userInfoCached: boolean;
  };
  refreshStatus: {
    refreshInProgress: boolean;
    queueLength: number;
  };
}

interface AuthError {
  code: string;
  message: string;
  details?: Record<string, any>;
  timestamp: Date;
}

interface CaptchaRequest {
  recaptcha_response?: string;
  token?: any;
}

interface CaptchaResponse {
  recaptcha_token?: string;
  success?: boolean;
}

interface LoginCredentials {
  username: string;
  password: string;
  grant_type: string;
}

interface UserResponse extends User {
  imported_user?: boolean;
  settings?: Record<string, any>;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private apiService = inject(ApiService);
  private router = inject(Router);
  private storage = inject(StorageService);
  private toastr = inject(ToasterService);

  // Angular Signals for reactive authentication state
  private isAuthenticatedSignal = signal<boolean>(false);
  private isCaptchaRequiredSignal = signal<boolean>(false);
  private navigationSignal = signal<any[]>([]);
  private leftNavigationSignal = signal<any[]>([]);

  // Public computed signals
  public isAuthenticated = computed(() => this.isAuthenticatedSignal());
  public isCaptchaRequired = computed(() => this.isCaptchaRequiredSignal());
  public navigation = computed(() => this.navigationSignal());
  public leftNavigation = computed(() => this.leftNavigationSignal());

  private destroy$ = new Subject<void>();
  public refreshSubscription: any;
  private refreshInProgress = false;
  private refreshQueue: RefreshQueueItem[] = [];
  redirectUrl: string;
  cachedRequests: Array<HttpRequest<any>> = [];

  constructor() {
    // Initialize authentication state from storage
    this.initializeAuthState();
  }

  /**
   * Initialize authentication state from storage on service startup
   */
  private initializeAuthState(): void {
    const token = this.getToken();
    const tokenValid = token && !this.isTokenExpired();
    this.isAuthenticatedSignal.set(!!tokenValid);

    if (tokenValid) {
      this.logAuthEvent('AUTH_STATE_RESTORED', { hasToken: true });
    }
  }

  /**
   * Retrieves the current captcha token from storage
   * @returns The captcha token or null if not found
   */
  getCaptchaToken(): string {
    return this.storage.retrieve('captchaToken');
  }

  /**
   * Retrieves the current authentication token from storage
   * @returns The JWT token or null if not found
   */
  getToken(): string {
    return this.storage.retrieve('authToken');
  }

  /**
   * Stores the authentication token in local storage
   * @param token - The JWT token to store
   */
  saveToken(token: string): void {
    if (!token || typeof token !== 'string' || token.trim().length === 0) {
      throw new Error('Invalid token provided');
    }
    this.storage.store('authToken', token);
  }

  getBuildKey(): string {
    return this.storage.retrieve('buildKey');
  }

  saveBuildKey(buildKey: string) {
    this.storage.store('buildKey', buildKey);
  }

  /**
   * Checks if the current authentication token has expired
   * @returns True if token is expired or missing, false otherwise
   */
  isTokenExpired(): boolean {
    const expiresAt = parseInt(this.storage.retrieve('exp_date'), 10);
    return Date.now() > expiresAt;
  }

  collectFailedRequest(request): void {
    this.cachedRequests.push(request);
  }


  // Verify token in localstorage with server & load user's info.
  // This runs once on application startup.
  populate() {
    // If token detected, attempt to get & store user's info
    if (this.getToken()) {
      const tokenExpired = this.isTokenExpired();
      if (tokenExpired) {
        // Remove any potential remnants of previous auth states
        this.purgeAuth();
      } else {
        const temp = { 'access_token': this.getToken() };
        this.setAuth(temp);
        this.startupTokenRefresh();
      }
    } else {
      // Remove any potential remnants of previous auth states
      this.purgeAuth();
    }
  }

  setAuth(data: AuthToken) {
    // Save token received from server in localstorage
    this.saveToken(data.access_token);
    // Set isAuthenticated to true
    this.isAuthenticatedSignal.set(true);
  }


  purgeAuth() {
    // Remove token from localstorage
    this.revokeTokenNoResponse();
    const captchatoken = this.storage.retrieve('captchaToken');
    this.storage.clearAll();
    this.storage.store('captchaToken', captchatoken);
    // Set auth status to false
    this.isAuthenticatedSignal.set(false);
    // Unschedule the token refresh
    this.unscheduleRefresh();
    // Clear cached data
  }

  /**
   * Attempts to authenticate user with provided credentials
   * @param type - The authentication type (usually 'password')
   * @param credentials - User login credentials
   * @returns Observable that emits the authentication token on success
   */
  attemptAuth(type: string, credentials: LoginCredentials): Observable<AuthToken> {
    // Input validation
    if (!credentials || !credentials.username || !credentials.password) {
      throw new Error('Invalid credentials: username and password are required');
    }
    if (credentials.username.length < 1 || credentials.password.length < 1) {
      throw new Error('Invalid credentials: username and password cannot be empty');
    }

    const creds = 'grant_type=password&password='
      + encodeURIComponent(credentials['password']) + '&username=' + encodeURIComponent(credentials['username']);
    return this.apiService.authPost('/token/', creds).pipe(
      map(
        data => {
          this.setAuth(data);
          const expiresAt: any = data.expires_in * 1000 + new Date().getTime();

          this.storage.store('exp_delay', data.expires_in.toString());
          this.storage.store('exp_date', expiresAt);
          this.storage.store('refresh_token', data.refresh_token);

          this.scheduleRefresh();
          return data;
        }
      ));
  }

  scheduleRefresh() {
    // If the user is authenticated, use the token stream
    const idToken = this.getToken();
    const source = observableOf(idToken).pipe(
      mergeMap(token => {
        // tslint:disable-next-line:radix
        let delay = parseInt(this.storage.retrieve('exp_delay'));
        const refreshTokenThreshold = TOKEN_REFRESH.THRESHOLD_SECONDS;
        delay = (delay - refreshTokenThreshold) * 1000;
        // tslint:disable-next-line:radix
        return observableInterval(delay);
      }),
      takeUntil(this.destroy$)
    );

    this.refreshSubscription = source.subscribe(() => {
      this.getNewAuthToken().pipe(
        takeUntil(this.destroy$)
      ).subscribe(
        (data) => {
          // Token refreshed successfully
        },
        (error) => {
          this.purgeAuth();
          this.logAuthEvent('TOKEN_REFRESH_ERROR', error);
          this.router.navigateByUrl('/login');
        }
      );
    });
  }

  startupTokenRefresh() {
    this.logAuthEvent('STARTUP_TOKEN_REFRESH');
    // If the user is authenticated, use the token stream
    const idToken = this.getToken();
    const tokenExpired = idToken ? this.isTokenExpired() : true;
    if (!tokenExpired) {
      const source = observableOf(idToken).pipe(
        mergeMap(token => {
          // Get the expiry time to generate
          const now: any = new Date().valueOf();

          // tslint:disable-next-line:radix
          const date: any = this.storage.retrieve('exp_date') ? parseInt(this.storage.retrieve('exp_date')) : 0;
          const temp = new Date(date).getTime();
          const refreshTokenThreshold = TOKEN_REFRESH.THRESHOLD_SECONDS; // seconds

          // a delay in milliseconds
          let delay: any = temp - now;
          (delay < refreshTokenThreshold) ? delay = 1 : delay = delay - refreshTokenThreshold;

          // Use the delay in a timer to
          // run the refresh at the proper time
          return observableTimer(delay);
        }),
        takeUntil(this.destroy$)
      );

      // Once the delay time from above is
      // reached, get a new token and schedule
      // additional refreshes
      source.subscribe(() => {
        this.getNewAuthToken().pipe(
          takeUntil(this.destroy$)
        ).subscribe(
          (res) => {
            this.scheduleRefresh();
          },
          (error) => {
            this.purgeAuth();
            this.logAuthEvent('TOKEN_REFRESH_ERROR', error);
            this.router.navigateByUrl('/login');
          }
        );
      });
    }
  }

  unscheduleRefresh() {
    // Unsubscribe from the refresh
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

  getNewAuthToken(): Observable<AuthToken> {
    // If refresh is already in progress, return a promise that resolves when current refresh completes
    if (this.refreshInProgress) {
      return new Observable(observer => {
        this.refreshQueue.push({
          resolve: (data) => {
            observer.next(data);
            observer.complete();
          },
          reject: (error) => {
            observer.error(error);
          }
        });
      });
    }

    // Mark refresh as in progress
    this.refreshInProgress = true;

    const refreshToken = this.storage.retrieve('refresh_token');
    const creds = 'grant_type=refresh_token&refresh_token=' + encodeURIComponent(refreshToken);

    return this.apiService.authPost('/token/', creds).pipe(
      retryWhen(errors =>
        errors.pipe(
          delay(TOKEN_REFRESH.RETRY_DELAY),
          take(TOKEN_REFRESH.RETRY_ATTEMPTS)
        )
      ),
      tap(data => {
        // Update auth data
        this.setAuth(data);
        this.storage.store('exp_delay', data.expires_in.toString());
        const expiresAt = (data.expires_in * 1000) + new Date().getTime();
        this.storage.store('exp_date', expiresAt);
        this.storage.store('refresh_token', data.refresh_token);

        // Resolve all queued promises
        this.refreshQueue.forEach(item => item.resolve(data));
        this.refreshQueue = [];
        this.refreshInProgress = false;
      }),
      catchError(error => {
        // Reject all queued promises
        this.refreshQueue.forEach(item => item.reject(error));
        this.refreshQueue = [];
        this.refreshInProgress = false;
        throw error;
      })
    );
  }


  getAuthUserDetails(): UserResponse | null {
    const user = this.storage.retrieve('user');
    return user || null;
  }

  revokeTokenNoResponse() {
    if (this.storage.retrieve('authtoken')) {
      const token = this.storage.retrieve('authtoken');
      const creds = 'token=' + encodeURIComponent(token);
      this.apiService.authPost('/revoke_token/', creds)
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }
  }

  revokeToken() {
    const token = this.storage.retrieve('authtoken');
    const creds = 'token=' + encodeURIComponent(token);

    return this.apiService.authPost('/revoke_token/', creds)
      .pipe(
        map(response => {
          return response;
        }));
  }

  validateCaptcha(data: CaptchaRequest): Observable<CaptchaResponse> {
    const url = '/validate_recaptcha';
    return this.apiService
      .post(url, data).pipe(
        map(response => {
          if (response.recaptcha_token) {
            this.storage.store('captchaToken', response.recaptcha_token);
            this.isCaptchaRequiredSignal.set(false);
          }
          return response;
        })
      );
  }

  resetPassword(data: { password: string; token: string }): Observable<any> {
    return this.apiService
      .post('/users/setpassword', data).pipe(
        map(response => {
          return response;
        }));
  }

  forgotPassword(data: { email: string }): Observable<any> {
    return this.apiService
      .post('/users/forgot_password', data).pipe(
        tap(
          _ => {
            this.toastr.success(
              ' Password reset link has been sent to your email '
            );
          },
          _ =>
            this.toastr.success(
              'Your Email is not registered with us'
            )
        )
      );
  }

  /**
   * Standardized error handling for auth operations
   */
  private handleAuthError(error: any, operation: string): AuthError {
    const authError: AuthError = {
      code: error.code || 'AUTH_ERROR',
      message: error.message || `Authentication error during ${operation}`,
      details: error,
      timestamp: new Date()
    };

    console.error(`Auth Service Error [${operation}]:`, authError);
    return authError;
  }

  /**
   * Log authentication events for debugging and monitoring
   */
  private logAuthEvent(event: string, details?: any): void {
    if (typeof console !== 'undefined') {
      console.log(`Auth Event [${event}]:`, {
        timestamp: new Date().toISOString(),
        details
      });
    }
  }

  setIsCaptchaRequired(value: boolean) {
    this.isCaptchaRequiredSignal.set(value);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.unscheduleRefresh();
  }
}
